import { Action, createReducer, on } from '@ngrx/store';
import * as ContractsActionTypes from '../../actions/contracts/contracts.actions';

import { PaginationRequest } from 'src/app/models/common/paginationRequest';
import { ContractDTO } from 'src/app/models/Contracts/contractDTO';
import { ContractPlafondMeasurementDTO } from 'src/app/models/Contracts/contractPlafondMeasurementDTO';
import { ContractPlafondRuleDTO } from 'src/app/models/Contracts/contractPlafondRuleDTO';
import { ProductGroupDTO } from 'src/app/models/Products/productDTO';
import { SiteDTO } from 'src/app/models/Sites/siteDTO';
import { CardsDTO } from 'src/app/models/Cards/cardDTO';
import { LimitIntervalDTO } from 'src/app/models/limits/limitIntervalDTO';
import { LimitMeasurementDTO } from 'src/app/models/limits/limitMeasurementDTO';
import { MovementDTO } from 'src/app/models/movements/movements';
import { ColumnSorterInfo } from 'src/app/models/custom-table/custom-table.model';

export interface ContractsState {
    plafondRules: ContractPlafondRuleDTO[];
    plafondMeasurements: ContractPlafondMeasurementDTO[];
    contractList: ContractDTO[];
    contract: ContractDTO;
    created: boolean;
    isLoading: boolean;
    error: Error;
    totalRecords: number;
    pageReq: PaginationRequest;
    contractMovementList: MovementDTO[];
    productGroups: ProductGroupDTO[];
    sites: SiteDTO[];
    limitIntervals: LimitIntervalDTO[];
    limitMeasurements: LimitMeasurementDTO[];
    cardList?: CardsDTO[]
    contractExcelList?: ContractDTO[];
    totalContracts?: number;
    contractTableConfig: ColumnSorterInfo[],
}

export const initialState: ContractsState = {
    plafondRules: null,
    plafondMeasurements: null,
    contractList: null,
    contract: null,
    created: false,
    isLoading: false,
    error: null,
    totalRecords: 0,
    pageReq: null,
    contractMovementList: null,
    productGroups: null,
    sites: null,
    limitIntervals: null,
    limitMeasurements: null,
    cardList: null,
    contractExcelList: null,
    totalContracts: null,
    contractTableConfig: null,
};

const contractsReducer = createReducer(
    initialState,
    on(ContractsActionTypes.resetContractsState, () => initialState),
    on(ContractsActionTypes.getPlafondRules, (state, { pageReq }) => ({
        ...state, error: null, pageReq: pageReq
    } as ContractsState)),
    on(ContractsActionTypes.loadPlafondRules, (state, { response }) => {
        let currentOffset = state.pageReq ? state.pageReq.offset : 0;
        let totalRecords = Math.max(currentOffset + (response as any).result.size, state.totalRecords);
        return {
            ...state, plafondRules: (response as any).result.items || [], error: null, totalRecords: totalRecords
        };
    }),
    on(ContractsActionTypes.getPlafondMeasurements, (state, { pageReq }) => ({
        ...state, error: null, pageReq: pageReq
    } as ContractsState)),
    on(ContractsActionTypes.loadPlafondMeasurements, (state, { response }) => {
        let currentOffset = state.pageReq ? state.pageReq.offset : 0;
        let totalRecords = Math.max(currentOffset + (response as any).result.size, state.totalRecords);
        return {
            ...state, plafondMeasurements: (response as any).result.items || [], error: null, totalRecords: totalRecords
        };
    }),
    on(ContractsActionTypes.getContractCardList, (state, { container, options }) => ({
        ...state, error: null, container: container, options: options
    } as ContractsState)),
    on(ContractsActionTypes.loadContractCardsList, (state, { response }) => {
        let currentOffset = state.pageReq ? state.pageReq.offset : 0;
        let totalRecords = Math.max(currentOffset + (response as any).result.size, state.totalRecords);
        return {
            ...state, cardList: (response as any).result || [], error: null, totalRecords: totalRecords
        };
    }),
    on(ContractsActionTypes.setLoadingContractsStartState, (state) => {
        return {
            ...state, isLoading: true, error: null
        };
    }),
    on(ContractsActionTypes.setLoadingContractsEndState, (state) => {
        return {
            ...state, isLoading: false, error: null
        };
    }),
    on(ContractsActionTypes.resetContract, (state) => {
        return {
            ...state, contract: null, error: null
        };
    }),
    on(ContractsActionTypes.setContract, (state, { contract }) => ({
        ...state, contract: (contract as any).result, error: null
    })),
    on(ContractsActionTypes.getContractList, (state, { options }) => {
        return {
          ...state,
          options: options,
          isLoading: true,
        }
      }),
      on(ContractsActionTypes.loadContractList, (state, { response }) => {
        return {
          ...state,
          contractList: (response as any).result?.items
            ? (response as any).result?.items
            : undefined,
          error: null,
          isLoading: false,
          totalContracts: (response as any)?.result?.size
            ? (response as any)?.result?.size
            : 0,
        };
      }),
      on(
        ContractsActionTypes.getContractExcelList,
        (state, { options }) =>
          ({
            ...state,
            error: null,
            options: options,
            isLoading: true,
          } as ContractsState)
      ),
      on(ContractsActionTypes.loadContractExcelList, (state, { response }) => {
        return {
          ...state,
          contractExcelList: (response as any)?.result?.items
            ? (response as any)?.result?.items
            : undefined,
          isLoading: false,
          error: null,
        };
      }),
    on(ContractsActionTypes.getContractTableConfig, (state) => {
      return {
        ...state,
        isLoading: true,
      }
    }),
    on(ContractsActionTypes.loadContractTableConfig, (state, { response }) => {
      return {
        ...state,
        contractTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
      };
    }),
    on(ContractsActionTypes.saveContractTableConfig, (state) => {
      return {
        ...state,
        isLoading: true,
      };
    }),
    on(ContractsActionTypes.loadContractMovementList, (state, { response }) => {
        return {
            ...state, contractMovementList: (response as any).result ? (response as any).result : undefined,
             error: null
        };
    }),
    on(ContractsActionTypes.getProductGroups, (state, { pageReq }) => ({
        ...state, error: null, pageReq: pageReq
    } as ContractsState)),
    on(ContractsActionTypes.loadProductGroups, (state, { response }) => {
        let currentOffset = state.pageReq ? state.pageReq.offset : 0;
        let totalRecords = Math.max(currentOffset + (response as any).result.size, state.totalRecords);
        return {
            ...state, productGroups: (response as any).result.items || [], error: null, totalRecords
        };
    }),
    on(ContractsActionTypes.loadSitesList, (state, { response }) => {
      return {
          ...state,
          sites: (response as any).result.items || [],
          error: null,
      };
    }),
    on(ContractsActionTypes.getLimitIntervals, (state, { pageReq }) => ({
        ...state, error: null, pageReq: pageReq
    } as ContractsState)),
    on(ContractsActionTypes.loadLimitIntervals, (state, { response }) => {
        // let currentOffset = state.pageReq ? state.pageReq.offset : 0;
        // let totalRecords = Math.max(currentOffset + (response as any).result.size, state.totalRecords);
        return {
            ...state, limitIntervals: (response as any).result.items || [], error: null,
        };
    }),
    on(ContractsActionTypes.getLimitMeasurements, (state, { pageReq }) => ({
        ...state, error: null, pageReq: pageReq
    } as ContractsState)),
    on(ContractsActionTypes.loadLimitMeasurements, (state, { response }) => {
        // let currentOffset = state.pageReq ? state.pageReq.offset : 0;
        // let totalRecords = Math.max(currentOffset + (response as any).result.size, state.totalRecords);
        return {
            ...state, limitMeasurements: (response as any).result.items || [], error: null,
        };
    }),
    on(ContractsActionTypes.getContract, (state) => {
      return {
        ...state,
        isLoading: true,
        created: false,
      };
    }),
    on(ContractsActionTypes.resetCreation, (state) => {
      return {
        ...state,
        created: true,
      };
    }),
    on(
      ContractsActionTypes.createContract,
      ContractsActionTypes.updateContract,
      ContractsActionTypes.updateContractProductGroups,
      ContractsActionTypes.updateContractLimits,
      ContractsActionTypes.resetContractLimit,
      ContractsActionTypes.resetContractPlafond,
      (state) => {
        return {
          ...state,
          isLoading: true,
        };
     }
    ),
);

export function reducer(state: ContractsState | undefined, action: Action) {

    return contractsReducer(state, action);
}
